import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Palette, Building2, Users, Plus, Loader2, Check, Send, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { applyBrand } from "../lib/brand";
import { AddMemberDialog } from "../components/team/AddMemberDialog";
import { MemberActions } from "../components/team/MemberActions";

const PRESETS = ["#6366F1", "#10B981", "#06B6D4", "#F59E0B", "#F43F5E", "#8B5CF6", "#EC4899", "#14B8A6"];
import { ROLE_LABELS, isManagerRole, canManage } from "../lib/roles";

export default function Settings() {
  const { tenant, subAccounts, activeSub, user, refreshTenant, refreshSubAccounts, switchSubAccount } = useAuth();
  const isAgency = ["agency_owner", "agency_admin"].includes(user?.role);
  const isManager = isManagerRole(user?.role);
  const [tname, setTname] = useState("");
  const [tcolor, setTcolor] = useState("#6366F1");
  const [users, setUsers] = useState([]);
  const [subOpen, setSubOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [subForm, setSubForm] = useState({ name: "", industry: "", timezone: "UTC" });
  const [saving, setSaving] = useState(false);
  const [tg, setTg] = useState({ connected: false });
  const [tgToken, setTgToken] = useState("");
  const [wa, setWa] = useState({ connected: false });
  const [waForm, setWaForm] = useState({ phone_number_id: "", waba_id: "", access_token: "" });
  const [phone, setPhone] = useState({ configured: false });
  const [areaCode, setAreaCode] = useState("");
  const [ownerFwd, setOwnerFwd] = useState("");

  useEffect(() => {
    if (tenant) { setTname(tenant.branding?.display_name || tenant.name); setTcolor(tenant.branding?.primary_color || "#6366F1"); }
  }, [tenant]);
  useEffect(() => { if (isManager) api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, [isManager]);
  useEffect(() => { if (isAgency) api.get("/telegram/settings").then(({ data }) => setTg(data)).catch(() => {}); }, [isAgency]);
  useEffect(() => { if (isAgency) api.get("/whatsapp/settings").then(({ data }) => setWa(data)).catch(() => {}); }, [isAgency]);
  useEffect(() => { if (isAgency) api.get("/phone/settings").then(({ data }) => { setPhone(data); setOwnerFwd(data.owner_forward || ""); }).catch(() => {}); }, [isAgency]);

  const saveTelegram = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/telegram/settings", { bot_token: tgToken, public_url: process.env.REACT_APP_BACKEND_URL });
      setTg(data); setTgToken("");
      toast.success(`Telegram connected: @${data.bot_username}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveWhatsapp = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/whatsapp/connect-manual", waForm);
      setWaForm({ phone_number_id: "", waba_id: "", access_token: "" });
      toast.success(`WhatsApp connected: ${data.display_phone || data.verified_name}`);
      api.get("/whatsapp/settings").then(({ data: d }) => setWa(d)).catch(() => {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const disconnectWhatsapp = async () => {
    try { await api.delete("/whatsapp/disconnect"); setWa({ connected: false }); toast.success("WhatsApp disconnected"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const savePhone = async () => {
    setSaving(true);
    try {
      await api.put("/phone/settings", { sub_account_id: phone.sub_account_id, owner_forward: ownerFwd, record_calls: !!phone.record_calls });
      toast.success("Phone settings saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const provisionNumber = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/phone/provision", { sub_account_id: phone.sub_account_id, area_code: areaCode || null });
      toast.success(`Number provisioned: ${data.number}`);
      api.get("/phone/settings").then(({ data: d }) => setPhone(d)).catch(() => {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveTenant = async () => {
    setSaving(true);
    try {
      await api.put("/tenant/branding", { display_name: tname, primary_color: tcolor });
      applyBrand(tcolor); await refreshTenant();
      toast.success("Agency branding updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveSubBranding = async (subId, color) => {
    await api.put(`/sub-accounts/${subId}/branding`, { primary_color: color });
    await refreshSubAccounts();
    if (activeSub?.id === subId) switchSubAccount(subId);
    toast.success("Client colour updated");
  };

  const createSub = async () => {
    setSaving(true);
    try {
      await api.post("/sub-accounts", subForm);
      toast.success("Sub-account created");
      setSubOpen(false); setSubForm({ name: "", industry: "", timezone: "UTC" });
      await refreshSubAccounts();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const loadUsers = () => api.get("/users").then(({ data }) => setUsers(data));

  return (
    <div className="space-y-6 animate-fade-up max-w-4xl">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">White-Label Engine</p>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Settings</h1>
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "branding"}>
        <TabsList>
          <TabsTrigger value="branding" className="gap-1.5" data-testid="tab-branding"><Palette className="w-4 h-4" />Branding</TabsTrigger>
          <TabsTrigger value="clients" className="gap-1.5" data-testid="tab-clients"><Building2 className="w-4 h-4" />Clients</TabsTrigger>
          {isManager && <TabsTrigger value="team" className="gap-1.5" data-testid="tab-team"><Users className="w-4 h-4" />Team</TabsTrigger>}
          {isAgency && <TabsTrigger value="telegram" className="gap-1.5" data-testid="tab-telegram"><Send className="w-4 h-4" />Telegram</TabsTrigger>}
          {isAgency && <TabsTrigger value="whatsapp" className="gap-1.5" data-testid="tab-whatsapp"><MessageCircle className="w-4 h-4" />WhatsApp</TabsTrigger>}
          {isAgency && <TabsTrigger value="phone" className="gap-1.5" data-testid="tab-phone"><Phone className="w-4 h-4" />Phone</TabsTrigger>}
        </TabsList>

        <TabsContent value="branding" className="mt-6 space-y-6">
          <Card className="p-6 bg-card border-border/70">
            <h3 className="font-display font-semibold text-lg mb-1">Agency brand</h3>
            <p className="text-sm text-muted-foreground mb-6">This name and accent colour represent your agency across Momento.</p>
            <div className="space-y-5">
              <div className="space-y-1.5"><Label>Agency display name</Label>
                <Input value={tname} onChange={(e) => setTname(e.target.value)} disabled={!isAgency} data-testid="agency-name-input" /></div>
              <div className="space-y-2"><Label>Primary accent colour</Label>
                <div className="flex flex-wrap gap-2.5">
                  {PRESETS.map((c) => (
                    <button key={c} onClick={() => setTcolor(c)} disabled={!isAgency}
                      className="w-9 h-9 rounded-lg flex items-center justify-center transition-transform hover:scale-110"
                      style={{ background: c }} data-testid={`color-preset-${c}`}>
                      {tcolor === c && <Check className="w-4 h-4 text-white" />}
                    </button>
                  ))}
                  <Input type="color" value={tcolor} onChange={(e) => setTcolor(e.target.value)} disabled={!isAgency}
                    className="w-9 h-9 p-1 cursor-pointer" data-testid="agency-color-input" />
                </div>
              </div>
              {isAgency && <Button onClick={saveTenant} disabled={saving} className="font-semibold" data-testid="save-branding-button">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save branding"}</Button>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="clients" className="mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">{subAccounts.length} client sub-account{subAccounts.length === 1 ? "" : "s"}</p>
            {isAgency && (
              <Dialog open={subOpen} onOpenChange={setSubOpen}>
                <DialogTrigger asChild><Button className="gap-2" data-testid="add-sub-account-button"><Plus className="w-4 h-4" />New client</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>New sub-account</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-1.5"><Label>Client name</Label><Input value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} data-testid="sub-name-input" /></div>
                    <div className="space-y-1.5"><Label>Industry</Label><Input value={subForm.industry} onChange={(e) => setSubForm({ ...subForm, industry: e.target.value })} data-testid="sub-industry-input" /></div>
                  </div>
                  <DialogFooter><Button onClick={createSub} disabled={saving || !subForm.name} data-testid="save-sub-account-button">Create client</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {subAccounts.map((s) => (
              <Card key={s.id} className="p-5 bg-card border-border/70" data-testid={`client-card-${s.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-bold text-white" style={{ background: s.branding?.primary_color || "#6366F1" }}>{s.name[0]}</div>
                  <div className="min-w-0"><p className="font-semibold truncate">{s.name}</p><p className="text-xs text-muted-foreground truncate">{s.industry || "—"} · {s.contact_count} contacts</p></div>
                </div>
                {isManager && (
                  <div className="flex flex-wrap gap-1.5">
                    {PRESETS.slice(0, 6).map((c) => (
                      <button key={c} onClick={() => saveSubBranding(s.id, c)} className="w-6 h-6 rounded-md transition-transform hover:scale-110" style={{ background: c }} data-testid={`sub-color-${s.id}-${c}`} />
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        {isManager && (
          <TabsContent value="team" className="mt-6 space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">{users.length} team member{users.length === 1 ? "" : "s"} · each person only sees the clients you give them</p>
              <Button className="gap-2" onClick={() => setUserOpen(true)} data-testid="add-user-button"><Plus className="w-4 h-4" />Add member</Button>
              <AddMemberDialog open={userOpen} onOpenChange={setUserOpen} subAccounts={subAccounts} onAdded={loadUsers} />
            </div>
            <Card className="bg-card border-border/70 divide-y divide-border/60">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-4 px-5 py-3.5" data-testid={`user-row-${u.id}`}>
                  <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold">{u.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</div>
                  <div className="min-w-0 flex-1"><p className="font-medium text-sm truncate">{u.name}</p><p className="text-xs text-muted-foreground truncate font-mono">{u.email}</p></div>
                  {u.pending_invite && <Badge variant="outline" data-testid={`user-pending-${u.id}`}>Invite pending</Badge>}
                  <Badge variant="secondary" data-testid={`user-role-${u.id}`}>{ROLE_LABELS[u.role] || u.role}</Badge>
                  {u.id !== user.id && canManage(user, u) ? <MemberActions member={u} subAccounts={subAccounts} onChanged={loadUsers} /> : <span className="w-9" />}
                </div>
              ))}
            </Card>
          </TabsContent>
        )}
        {isAgency && (
          <TabsContent value="telegram" className="mt-6">
            <Card className="p-6 bg-card border-border/70 max-w-xl">
              <h3 className="font-display font-semibold text-lg mb-1">Telegram (free messaging)</h3>
              <p className="text-sm text-muted-foreground mb-5">
                Connect a Telegram bot to message contacts for free — a zero-cost alternative to SMS. Create a bot with
                <span className="text-foreground font-medium"> @BotFather</span>, then paste its token below. Contacts connect via a
                personal link (from the Conversations screen) or by sharing their phone with your bot.
              </p>
              {tg.connected ? (
                <div className="flex items-center gap-2 mb-5 text-sm">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span>Connected as <span className="font-mono text-emerald-400">@{tg.bot_username}</span></span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-5 text-sm text-muted-foreground">
                  <span className="w-2 h-2 rounded-full bg-muted-foreground/50" /> Not connected
                </div>
              )}
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Bot token</Label>
                  <Input value={tgToken} onChange={(e) => setTgToken(e.target.value)} placeholder="123456:ABC-DEF…" className="font-mono text-sm" data-testid="telegram-token-input" /></div>
                <Button onClick={saveTelegram} disabled={saving || !tgToken} className="font-semibold" data-testid="save-telegram-button">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (tg.connected ? "Update token" : "Connect Telegram")}
                </Button>
              </div>
            </Card>
          </TabsContent>
        )}
        {isAgency && (
          <TabsContent value="whatsapp" className="mt-6">
            <Card className="p-6 bg-card border-border/70 max-w-xl">
              <h3 className="font-display font-semibold text-lg mb-1">WhatsApp (send offers to leads)</h3>
              <p className="text-sm text-muted-foreground mb-5">
                Connect your WhatsApp Business number to send offer broadcasts (approved templates) and get replies in your inbox.
                Paste the <span className="text-foreground font-medium">Phone Number ID</span>, <span className="text-foreground font-medium">WhatsApp Business Account ID</span> and an <span className="text-foreground font-medium">access token</span> from your Meta WhatsApp app.
              </p>
              {wa.connected ? (
                <>
                  <div className="flex items-center gap-2 mb-4 text-sm">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span>Connected: <span className="font-mono text-emerald-400">{wa.display_phone || wa.verified_name}</span></span>
                  </div>
                  <Button variant="outline" onClick={disconnectWhatsapp} data-testid="disconnect-whatsapp-button">Disconnect</Button>
                </>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-1.5"><Label>Phone Number ID</Label>
                    <Input value={waForm.phone_number_id} onChange={(e) => setWaForm({ ...waForm, phone_number_id: e.target.value })} placeholder="1234567890" className="font-mono text-sm" data-testid="wa-phone-id-input" /></div>
                  <div className="space-y-1.5"><Label>WhatsApp Business Account ID (WABA)</Label>
                    <Input value={waForm.waba_id} onChange={(e) => setWaForm({ ...waForm, waba_id: e.target.value })} placeholder="1234567890" className="font-mono text-sm" data-testid="wa-waba-id-input" /></div>
                  <div className="space-y-1.5"><Label>Access token</Label>
                    <Input value={waForm.access_token} onChange={(e) => setWaForm({ ...waForm, access_token: e.target.value })} placeholder="EAAG…" type="password" className="font-mono text-sm" data-testid="wa-token-input" /></div>
                  <Button onClick={saveWhatsapp} disabled={saving || !waForm.phone_number_id || !waForm.waba_id || !waForm.access_token} className="font-semibold" data-testid="save-whatsapp-button">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect WhatsApp"}
                  </Button>
                </div>
              )}
              {wa.webhook_url && (
                <div className="mt-6 pt-5 border-t border-border/60 space-y-2">
                  <p className="text-xs font-semibold text-foreground">Webhook setup (Meta → WhatsApp → Configuration)</p>
                  <div className="space-y-1.5"><Label className="text-xs">Callback URL</Label>
                    <Input readOnly value={wa.webhook_url} className="font-mono text-xs" data-testid="wa-webhook-url" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Verify token</Label>
                    <Input readOnly value={wa.verify_token} className="font-mono text-xs" data-testid="wa-verify-token" /></div>
                </div>
              )}
            </Card>
          </TabsContent>
        )}
        {isAgency && (
          <TabsContent value="phone" className="mt-6">
            <Card className="p-6 bg-card border-border/70 max-w-xl">
              <h3 className="font-display font-semibold text-lg mb-1">Phone & Calling</h3>
              <p className="text-sm text-muted-foreground mb-5">Get a business number that rings your cell first and logs every missed call on the contact — so you never lose a job.</p>
              {!phone.configured && (
                <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-300" data-testid="phone-not-configured">
                  <Phone className="w-4 h-4 mt-0.5 shrink-0" /><span>Twilio isn't connected yet. Add your Twilio credentials to enable buying numbers and calling.</span>
                </div>
              )}
              {phone.number ? (
                <div className="flex items-center gap-2 mb-5 text-sm"><Check className="w-4 h-4 text-emerald-400" /><span>Active number: <span className="font-mono text-emerald-400" data-testid="phone-number">{phone.number}</span></span></div>
              ) : (
                <div className="flex items-end gap-2 mb-5">
                  <div className="flex-1 space-y-1.5"><Label>Area code (optional)</Label>
                    <Input value={areaCode} onChange={(e) => setAreaCode(e.target.value)} placeholder="415" className="font-mono text-sm" data-testid="phone-areacode-input" /></div>
                  <Button onClick={provisionNumber} disabled={saving || !phone.configured || !phone.sub_account_id} data-testid="provision-number-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Get a number"}</Button>
                </div>
              )}
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Ring this number first (owner's cell)</Label>
                  <Input value={ownerFwd} onChange={(e) => setOwnerFwd(e.target.value)} placeholder="+14155550123" className="font-mono text-sm" data-testid="phone-owner-input" /></div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={!!phone.record_calls} onChange={(e) => setPhone({ ...phone, record_calls: e.target.checked })} data-testid="phone-record-toggle" />Record calls (with consent)</label>
                <Button onClick={savePhone} disabled={saving || !phone.sub_account_id} className="font-semibold" data-testid="save-phone-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save phone settings"}</Button>
              </div>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
